import z from 'zod'
import { pgTable, serial, integer, text, timestamp, jsonb, pgEnum } from 'drizzle-orm/pg-core'
import { createSelectSchema } from 'drizzle-zod'
import { pgUser } from './user'
import { pgBrand } from './brand'
import { zRemoteFileWithUrl } from '../typeRoots/fileWithUrl'

// MOODBOARD (placeholder name — see also tRPC router `moodboard`).
// Replace with the final product name (greppable as `moodboard` / `moodboard` / `MOODBOARD`).
// See backend/scripts/moodboard-spike.ts for the prose-vs-structured spike that motivated this.

// An item in the input pile. Discriminated union so the analyzer can treat each kind correctly.
//
//  - kind: 'reference' → fed to the analyzer agent as material to read and synthesize
//  - kind: 'instruction' → passed through verbatim; never re-summarized by the agent
//
// The analyzer composes the final prose from both kinds.

// World-space coordinates on the canvas. Top-left of the item's bounding box.
export const zCanvasPoint = z.object({ x: z.number(), y: z.number() })
export type CanvasPoint = z.infer<typeof zCanvasPoint>

// Item size in world-space pixels. Stored per item so the canvas doesn't have to invent
// defaults every load and so future resize handles have somewhere to write.
export const zCanvasSize = z.object({ w: z.number().positive(), h: z.number().positive() })
export type CanvasSize = z.infer<typeof zCanvasSize>

// Per-item posture toward the brand. Lets the analyzer treat items differently:
// - reference (default) → material to absorb into the voice / positioning
// - hero → carries extra weight; e.g. flagship visual, canonical sample post
// - antiPattern → informs `watchFor` rather than positive guidance
// - competitor → informs positioning (what the brand differentiates against)
export const zMoodboardPolarity = z.enum(['reference', 'hero', 'antiPattern', 'competitor'])
export type MoodboardPolarity = z.infer<typeof zMoodboardPolarity>

// What a moodboard item is *for*. This is orthogonal to `kind` (reference vs
// instruction) and `polarity` (how to weight it):
// - brief   → distilled into the static brand brief at Analyze time (voice, design).
//             This is the default and matches all pre-existing items.
// - content → NOT distilled into the brief. Fetched fresh at generation time and
//             injected as post material ("what's new"). Kept out of the analyzer so
//             it never contaminates the voice/design brief.
// Undefined is treated as 'brief' everywhere (see `role ?? 'brief'`).
export const zMoodboardRole = z.enum(['brief', 'content'])
export type MoodboardRole = z.infer<typeof zMoodboardRole>

// Marks an item the onboarding starter board seeded and the user has not touched yet.
// `slot` says which starter element it is, `source` whether it holds the generic
// template copy or content derived from the user's website. A user edit drops the
// marker, which is how the website fill and a website change know what they may
// overwrite. Untouched `generic` text is placeholder copy, so the analyzer skips it.
// See lib/onboarding/starterTemplate.ts. JSONB, no migration.
export const zMoodboardSeed = z.object({
  slot: z.enum(['website', 'voice', 'audience', 'invite', 'logo', 'color']),
  source: z.enum(['generic', 'website']),
})
export type MoodboardSeed = z.infer<typeof zMoodboardSeed>

const zMoodboardItemBase = z.object({
  id: z.string(),
  // ISO timestamp string. Stored as a JSONB primitive, so we don't coerce to Date —
  // that would lie about the runtime shape on the way back to clients.
  createdAt: z.string(),
  position: zCanvasPoint,
  size: zCanvasSize,
  // Polarity + annotation are user-supplied per-item metadata. Optional — undefined
  // polarity is treated as 'reference'.
  polarity: zMoodboardPolarity.optional(),
  annotation: z.string().optional(),
  // See zMoodboardRole. Optional — undefined is treated as 'brief'. JSONB, no migration.
  role: zMoodboardRole.optional(),
  seed: zMoodboardSeed.optional(),
})

export const zMoodboardTextItem = zMoodboardItemBase.extend({
  type: z.literal('text'),
  kind: z.enum(['reference', 'instruction']),
  text: z.string().min(1),
  // Visual-only background tint. Not passed to the analyzer — purely organizational
  // so users can colour-code notes on the canvas. `undefined` falls back to the
  // kind-specific default (white card for reference, transparent for instruction).
  backgroundColor: z.string().optional(),
})

// Font metadata parsed from the binary (client-side, at upload) for font files.
// `family` is the real typeface name from the font's name table, used as the
// canonical family everywhere (picker, autodesign prompt, Playwright, nodes)
// instead of the filename. Absent on older items / non-fonts → callers fall back
// to the filename-derived name. weight/style are stored for future weight
// grouping. JSONB, so no migration.
export const zFontMeta = z.object({
  family: z.string(),
  weight: z.number().optional(),
  style: z.enum(['normal', 'italic']).optional(),
})
export type FontMeta = z.infer<typeof zFontMeta>

export const zMoodboardFileItem = zMoodboardItemBase.extend({
  type: z.literal('file'),
  kind: z.literal('reference'),
  file: zRemoteFileWithUrl,
  // Cached Gemini analysis of video files. Populated by the analyzer's pre-extract stage
  // so the 3 parallel department agents share the same expensive analysis instead of
  // re-running it. JSONB so no migration needed.
  videoAnalysisProse: z.string().nullable().optional(),
  // Pre-rendered preview image (currently used for PDF page 1). Generated client-side at
  // upload time and uploaded as a sibling asset so the canvas can paint instantly on
  // reloads instead of running the lazy renderer for first paint.
  thumbnailUrl: z.string().url().optional(),
  // Parsed font metadata (font files only). See zFontMeta.
  fontMeta: zFontMeta.optional(),
})

// Logo candidate persisted on a URL item. Mirrors `LogoCandidate` from the
// backend extractor — kept loose here so we can store dataUrls without dragging
// in the full Playwright type. Schema validates basic shape only.
export const zMoodboardLogoCandidate = z.object({
  url: z.string().optional(),
  svg: z.string().optional(),
  dataUrl: z.string().optional(),
  type: z.enum(['img', 'svg', 'favicon', 'og-image']),
  score: z.number(),
  source: z.string(),
  width: z.number().optional(),
  height: z.number().optional(),
})
export type MoodboardLogoCandidate = z.infer<typeof zMoodboardLogoCandidate>

export const zMoodboardUrlItem = zMoodboardItemBase.extend({
  type: z.literal('url'),
  kind: z.literal('reference'),
  url: z.string().url(),
  // Optional cached scrape — populated by the analyzer so we don't re-fetch on re-run.
  scrapedTitle: z.string().nullable().optional(),
  scrapedContent: z.string().nullable().optional(),
  // URLs that were actually scraped (homepage + LLM-picked subpages, after the
  // legal-page filter). Surfaced on the link card so users can see what
  // material informed the analysis — and notice when nothing useful made it
  // past the filter.
  scrapedPages: z.array(z.string()).optional(),
  // Brand colors detected on the page (hex strings), populated by the analyzer in parallel
  // with the scrape. Rendered as swatches on the link card.
  extractedColors: z.array(z.string()).optional(),
  // Ranked logo candidates from Playwright. Cached on the item so the LinkCard
  // can offer them as clickable thumbnails after the first Analyze.
  extractedLogos: z.array(zMoodboardLogoCandidate).optional(),
})

// Hex like `#aabbcc` (6 chars) or `#abc` (3 chars). Kept loose — we don't store rgba.
const zHex = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/)

// User-curated brand color. Distinct from a URL's `extractedColors` — those are
// best-effort guesses, these are the user's explicit pick.
export const zMoodboardColorItem = zMoodboardItemBase.extend({
  type: z.literal('color'),
  kind: z.literal('reference'),
  hex: zHex,
  label: z.string().optional(),
})

// A live connector source: points the agent at a brand connection (GitHub; Notion later),
// scoped to a specific target (a repo). Like a URL content item it's read fresh at generation
// time and never distilled into the brief — but via the connector's API tools rather than a
// scrape. It also gates access: the interactive agent only gets a provider's tools when the
// moodboard has a connector for it, scoped to the target(s) here. The brand must have the
// matching connection for it to resolve.
export const zMoodboardConnectorItem = zMoodboardItemBase.extend({
  type: z.literal('connector'),
  kind: z.literal('reference'),
  provider: z.enum(['github', 'notion', 'google_calendar']),
  // Provider target. GitHub: "owner/name". Notion: a page or database id. Google Calendar: a
  // calendar id (e.g. an email address or "...@group.calendar.google.com").
  target: z.string().min(1),
  // Optional display label (defaults to the target). For Notion this holds the page/DB title,
  // for Google Calendar the calendar name, since both target ids are opaque.
  label: z.string().optional(),
})

export const zMoodboardItem = z.discriminatedUnion('type', [
  zMoodboardTextItem,
  zMoodboardFileItem,
  zMoodboardUrlItem,
  zMoodboardColorItem,
  zMoodboardConnectorItem,
])
export type MoodboardItem = z.infer<typeof zMoodboardItem>

// Analyzer brief — a structured-but-modular write-up of the brand.
// Each block is one moodboard-style facet. The agent picks which blocks to include and
// in what order. Some blocks are typed for visual + downstream reasons (palette feeds
// generateCanvas, watchFor renders with warning markers). `prose` and `bullets` are
// escape hatches with agent-supplied labels for anything off-pattern.
export const zMoodboardBriefBlock = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('throughline'),
    text: z.string(),
  }),
  z.object({
    type: z.literal('palette'),
    swatches: z.array(
      z.object({
        hex: z.string(),
        name: z.string().nullable().optional(),
        role: z.string().nullable().optional(),
      }),
    ),
  }),
  z.object({
    type: z.literal('audiences'),
    cards: z.array(
      z.object({
        title: z.string(),
        description: z.string(),
      }),
    ),
  }),
  z.object({
    type: z.literal('keyPhrases'),
    phrases: z.array(z.string()),
  }),
  z.object({
    type: z.literal('watchFor'),
    items: z.array(z.string()),
  }),
  z.object({
    type: z.literal('notes'),
    text: z.string(),
  }),
  z.object({
    type: z.literal('prose'),
    label: z.string(),
    content: z.string(),
  }),
  z.object({
    type: z.literal('bullets'),
    label: z.string(),
    items: z.array(z.string()),
  }),
])
export type MoodboardBriefBlock = z.infer<typeof zMoodboardBriefBlock>

export const zMoodboardBrief = z.object({
  blocks: z.array(zMoodboardBriefBlock),
})
export type MoodboardBrief = z.infer<typeof zMoodboardBrief>

// Per-department analyst output cache. Each entry is keyed by content hash + model tag
// so when items change we invalidate only the affected layer. Stored as a single JSONB
// column rather than 3 separate columns so adding a 4th department later is a code-only
// change. Departments are intentionally a closed enum for now.
export const zMoodboardDepartment = z.enum(['strategy', 'voice', 'design'])
export type MoodboardDepartment = z.infer<typeof zMoodboardDepartment>

// Analyzer metadata shared with the frontend (the analyze-config toggle tiles + tooltips).
// The backend registry in moodboardAnalyzer.ts pairs these ids with their run functions, so
// adding an analyzer later means: extend the department enum, add an entry here, add its
// runner + analyst file, and teach the summarizer to weave its memo in.
export const MOODBOARD_ANALYZER_META: {
  id: MoodboardDepartment
  label: string
  description: string
}[] = [
  {
    id: 'strategy',
    label: 'Strategy',
    description: 'Positioning, target audiences, and the brand throughline.',
  },
  {
    id: 'voice',
    label: 'Voice',
    description: 'Tone, rhythm, point of view, and signature vocabulary for the post writer.',
  },
  {
    id: 'design',
    label: 'Design',
    description: 'Palette, visual style, and typography — feeds image generation.',
  },
]

// Canonical analyzer id list, used as the default when a board has no explicit selection.
export const ALL_MOODBOARD_ANALYZERS: MoodboardDepartment[] = MOODBOARD_ANALYZER_META.map(
  (a) => a.id,
)

export const zMoodboardDeptCacheEntry = z.object({
  hash: z.string(),
  modelTag: z.string(),
  memo: z.string(),
  generatedAt: z.string(),
})
export type MoodboardDeptCacheEntry = z.infer<typeof zMoodboardDeptCacheEntry>

export const zMoodboardDeptCache = z.object({
  strategy: zMoodboardDeptCacheEntry.optional(),
  voice: zMoodboardDeptCacheEntry.optional(),
  design: zMoodboardDeptCacheEntry.optional(),
})
export type MoodboardDeptCache = z.infer<typeof zMoodboardDeptCache>

// Lifecycle of a moodboard's brief. Lets the picker / home distinguish a board
// that has never been analyzed (draft) from one whose brief is being generated
// in the background (analyzing) from a ready-to-use one (ready). `failed` lets
// the UI surface a retry affordance instead of a perpetual spinner.
//  - draft: no brief yet, or items changed and not re-analyzed
//  - analyzing: a brief generation run is in flight
//  - ready: analysisBrief is populated and current
//  - failed: the last generation run errored
export const MOODBOARD_STATUSES = ['draft', 'analyzing', 'ready', 'failed'] as const
export type MoodboardStatus = (typeof MOODBOARD_STATUSES)[number]
export const zMoodboardStatus = z.enum(MOODBOARD_STATUSES)
export const pgMoodboardStatus = pgEnum('moodboard_status', MOODBOARD_STATUSES)

export const pgMoodboard = pgTable('moodboard', {
  id: serial('id').primaryKey(),
  userId: integer('user_id')
    .notNull()
    .references(() => pgUser.id, { onDelete: 'cascade' }),
  brandId: integer('brand_id')
    .notNull()
    .references(() => pgBrand.id, { onDelete: 'cascade' }),

  name: text('name').notNull(),
  items: jsonb('items').$type<MoodboardItem[]>().notNull().default([]),

  // Brief lifecycle. Defaults to 'draft'; the migration backfills existing rows
  // with a populated analysisBrief to 'ready'.
  status: pgMoodboardStatus('status').notNull().default('draft'),

  // Cached analyzer output (structured brief). Null until first analyze.
  analysisBrief: jsonb('analysis_brief').$type<MoodboardBrief>(),
  // Per-department prose memos that fed the summarizer. Each entry caches by content
  // hash + model tag so the orchestrator can short-circuit unchanged departments
  // independently of the brief. Null until first analyze.
  analysisDeptCache: jsonb('analysis_dept_cache').$type<MoodboardDeptCache>(),
  // Which analyzer departments run on Analyze. Null = all (the default). Persisted per
  // moodboard so the user's toggle selection is remembered across sessions.
  enabledAnalyzers: jsonb('enabled_analyzers').$type<MoodboardDepartment[]>(),
  // sha256 of the inputs that produced analysisBrief. Null until first analyze.
  // When the current hash of items diverges, analysisBrief is stale.
  analysisHash: text('analysis_hash'),
  analysisGeneratedAt: timestamp('analysis_generated_at'),

  createdAt: timestamp('created_at').notNull().defaultNow(),
  updatedAt: timestamp('updated_at').notNull().defaultNow(),
})

export const zMoodboard = createSelectSchema(pgMoodboard).extend({
  items: zMoodboardItem.array(),
  status: zMoodboardStatus,
  analysisBrief: zMoodboardBrief.nullable(),
  analysisDeptCache: zMoodboardDeptCache.nullable(),
  enabledAnalyzers: zMoodboardDepartment.array().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  analysisGeneratedAt: z.coerce.date().nullable(),
})
export type Moodboard = z.infer<typeof zMoodboard>
